// 어드민 새 기능 안내 — 표 admin_feature_notice·admin_feature_ack 를 한 번 읽어(RPC admin_feature_notice_overview)
// 사이드바 배지·대시보드 한 줄·화면 띠·창 안 말풍선·「새 소식」 원장이 같은 데이터를 본다. AdminLayout 이 감싼다.
// 프로바이더 밖(dev 미리보기 등)에서는 빈 값 — 어떤 안내도 그리지 않는다.
// 시안: docs/design/admin-feature-notice-variants.html · 문서: docs/pages/admin/AdminWhatsNew.md
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../../lib/supabaseClient";
import { allTipsSeen, mergeTipSeen, readTipProgress, serverTipSeen, writeTipProgress } from "../../../lib/adminFeatureNotice";

const EMPTY = {
  notices: [],
  adminTotal: 0,
  me: null,
  loading: false,
  ack: async () => {},
  refresh: async () => {},
  tipSeen: {},
  tipMounted: {},
  registerTip: () => () => {},
  tipNext: () => {},
  tipDone: async () => {},
};

const Ctx = createContext(EMPTY);

export function AdminFeatureNoticeProvider({ children }) {
  const [state, setState] = useState({ notices: [], adminTotal: 0, me: null, loading: true });
  // 2026-09-27: 순번 대신 「넘긴 말풍선」과 「지금 화면에 찍힌 자리」로 고른다(pickVisibleTip) — 앞 말풍선 자리가 다른 탭에 있어도 뒤 말풍선이 뜬다
  // 2026-10-15: 「넘긴 말풍선」은 메모리가 아니라 기기 로컬(계정별) + 서버(kind 'tip:<anchor>') 에 남는다 — 새로고침·재진입에도 다시 안 뜬다
  const [tipSeen, setTipSeen] = useState({}); // noticeKey → 넘긴 anchor[] (「닫기」·「건너뛰기」·전부 넘김이면 ['*'])
  const [tipMounted, setTipMounted] = useState({}); // noticeKey → { anchor: 찍힌 개수 }
  const stateRef = useRef(state);
  stateRef.current = state;
  const tipSeenRef = useRef(tipSeen);
  tipSeenRef.current = tipSeen;

  const registerTip = useCallback((key, anchor) => {
    const bump = (d) => setTipMounted((m) => {
      const cur = { ...(m[key] || {}) };
      const n = (cur[anchor] || 0) + d;
      if (n > 0) cur[anchor] = n; else delete cur[anchor];
      return { ...m, [key]: cur };
    });
    bump(1);
    return () => bump(-1);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc("admin_feature_notice_overview");
      if (error || !data) {
        setState((s) => ({ ...s, loading: false }));
        return;
      }
      const notices = Array.isArray(data.notices) ? data.notices : [];
      const me = data.me || null;
      setState({ notices, adminTotal: Number(data.admin_total) || 0, me, loading: false });
      // 말풍선 진행도 — 서버(내가 넘긴 자리·닫음) + 기기 로컬 + 이번 세션. 같은 틱에 넣어 첫 그림 전에 갖춰진다.
      setTipSeen((cur) => mergeTipSeen(mergeTipSeen(cur, readTipProgress(me)), serverTipSeen(notices, me)));
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // 「확인했어요」(kind notice) / 말풍선 닫음(kind tip). 낙관적으로 먼저 지우고 서버에 남긴다 — 실패하면 다시 읽는다.
  const ack = useCallback(
    async (key, kind = "notice") => {
      const field = kind === "tip" ? "tip_acked_by" : "acked_by";
      setState((s) => ({
        ...s,
        notices: s.notices.map((n) =>
          n.key === key && s.me && !(n[field] || []).includes(s.me) ? { ...n, [field]: [...(n[field] || []), s.me] } : n
        ),
      }));
      const { error } = await supabase.rpc("admin_feature_ack", { p_key: key, p_kind: kind });
      if (error) await refresh();
    },
    [refresh]
  );

  // 진행도를 메모리·기기 로컬에 같이 남긴다
  const commitSeen = useCallback((key, anchors) => {
    setTipSeen((s) => {
      const next = mergeTipSeen(s, { [key]: anchors });
      writeTipProgress(stateRef.current.me, next);
      return next;
    });
  }, []);

  const tipNext = useCallback(
    (key, anchor) => {
      const { notices } = stateRef.current;
      const notice = notices.find((n) => n.key === key);
      // 전부 넘겼으면 「닫기」와 같다 — 서버에 kind='tip' 으로 닫음 기록
      const seenAfter = mergeTipSeen({ [key]: tipSeenRef.current[key] || [] }, { [key]: [anchor] })[key];
      if (notice && allTipsSeen(notice, seenAfter)) {
        commitSeen(key, ["*"]);
        ack(key, "tip");
        return;
      }
      commitSeen(key, [anchor]);
      // 서버에도 자리 단위로 — 마이그레이션 전 DB(kind 는 notice/tip 만) 는 거부하지만 로컬이 받쳐 주므로 오류는 무시
      Promise.resolve(supabase.rpc("admin_feature_ack", { p_key: key, p_kind: `tip:${anchor}` })).catch(() => {});
    },
    [ack, commitSeen]
  );
  const tipDone = useCallback(
    async (key) => {
      commitSeen(key, ["*"]);
      await ack(key, "tip");
    },
    [ack, commitSeen]
  );

  const value = useMemo(
    () => ({ ...state, ack, refresh, tipSeen, tipMounted, registerTip, tipNext, tipDone }),
    [state, ack, refresh, tipSeen, tipMounted, registerTip, tipNext, tipDone]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAdminFeatureNotices() {
  return useContext(Ctx);
}
